import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export const Monogram = ({ size = "h-10 w-10", textSize = "text-[11px]" }) => (
  <div className={`${size} flex items-center justify-center border border-gold/60`} aria-hidden="true">
    <span className={`font-serif ${textSize} tracking-[0.15em] text-ivory`}>SLS</span>
  </div>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-40 border-b transition-all duration-500 ${scrolled ? "border-gold/20 bg-navy/95 backdrop-blur-md" : "border-gold/10 bg-navy/90 backdrop-blur-sm"}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        <Link to="/" data-testid="nav-brand-link" className="group flex items-center gap-3">
          <Monogram />
          <span className="leading-none">
            <span className="block font-serif text-lg tracking-[0.08em] text-ivory">SMART</span>
            <span className="mt-1 block font-mono text-[8.5px] uppercase tracking-[0.42em] text-gold/90">Legal Solutions</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group relative font-mono text-[10.5px] uppercase tracking-[0.22em] transition-colors duration-300 ${isActive ? "text-gold" : "text-ivory/60 hover:text-ivory"}`
              }
            >
              {l.label}
              <span className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${pathname === l.to ? "w-full" : "w-0 group-hover:w-full"}`} />
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-contact-office-btn" className="border border-gold/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ivory transition-colors duration-300 hover:bg-gold hover:text-navy">
            Contact Office
          </Link>
        </nav>

        <button data-testid="nav-mobile-menu-btn" onClick={() => setOpen(!open)} aria-label="Toggle menu" className="text-ivory lg:hidden">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-gold/10 bg-navy px-5 pb-8 pt-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `border-b border-ivory/5 py-3.5 font-mono text-xs uppercase tracking-[0.25em] ${isActive ? "text-gold" : "text-ivory/70"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-mobile-contact-btn" className="mt-5 border border-gold/50 py-3 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-ivory">
              Contact Office
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
